import { FC, useEffect, useState } from 'react'
import { produce } from 'immer'
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem
} from '@progress/kendo-react-charts'

import { ErrorMessage, ErrorMessages, SupplierHearingHeader } from '../../../components/parts'
import { ReplyAggregationGraphFooter } from './ReplyAggregationGraphFooter'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { useAppSelector, useAppDispatch, CommonActions } from '../../../stores'
import { HearingReplyItemSummaryGateway } from '../../../gateway/supplier/HearingReplyItemSummaryGateway'
import { Authority } from '../../../gateway/system/Authority'
import { CodeName } from '../../../services/initializing/CodeName'

interface SeriesType {
  series: Array<{
    name: string
    data: Array<number | null>
    color: string
  }>
}

export const ReplyAggregationGraph: FC = () => {
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('業者ヒアリング回答集計グラフ')
  const dispatch = useAppDispatch()
  const { regStatus } = useAppSelector((state) => state.confirmreply)
  const {
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    supplierNm,
    expense,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)
  const startYear = Number(displayStartYear)
  const years = [...new Array(6)].map((_, x) => startYear + x)
  const months = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
  const categories: any = []
  years.forEach((my) => {
    months.forEach((mm) => {
      categories.push(my.toString() + '/' + mm.toString())
    })
  })
  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === expenseCd
  })?.TXT8

  const userProp = useAppSelector((state) => state.common.userProp)

  const initSeries = [
    {
      name: '大成決定物件',
      data: [],
      color: '#E46C0A'
    },
    {
      name: '大成見積中物件',
      data: [],
      color: '#FFE162'
    },
    {
      name: '竣工済物件',
      data: [],
      color: '#99FF99'
    },
    {
      name: '非公開決定物件',
      data: [],
      color: '#4B5FFA'
    },
    {
      name: '非公開未決定物件',
      data: [],
      color: '#AC58FF'
    },
    {
      name: '他社物件-決定',
      data: [],
      color: '#FF5892'
    },
    {
      name: '他社物件-予定',
      data: [],
      color: '#99CCFF'
    }
  ]

  const initSeriesForSupplier = [
    {
      name: '大成決定物件',
      data: [],
      color: '#E46C0A'
    },
    {
      name: '大成見積中物件',
      data: [],
      color: '#FFE162'
    },
    {
      name: '竣工済物件',
      data: [],
      color: '#99FF99'
    },
    {
      name: '他社物件-決定',
      data: [],
      color: '#FF5892'
    },
    {
      name: '他社物件-予定',
      data: [],
      color: '#99CCFF'
    }
  ]

  const [series, setSeries] = useState<SeriesType['series']>(userProp?.authority === Authority.Supplier ? initSeriesForSupplier : initSeries)

  const [series2, setSeries2] = useState<SeriesType['series']>([
    {
      name: '貴社最大能力',
      data: [],
      color: '#FF0000'
    },
    {
      name: '大成対応可能数',
      data: [],
      color: '#00B050'
    }
  ])

  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      regStatus === undefined ||
      regStatus === null ||
      simulationYm === '' ||
      supplierCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === '' ||
      supplierBranchCd === ''
    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd
  ])

  useEffect(() => {
    let ignore = false

    dispatch(CommonActions.setIsLoading(true))
    /** 業者ヒアリング回答確認-サマリ取得 */
    HearingReplyItemSummaryGateway.getSupplierHearingReplyItemSummary(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      1,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          if (!ignore) {
            setSeries(
              produce((draft) => {
                if (userProp?.authority === Authority.Supplier) {
                  draft[0].data = res.data.supplierDecisionBuilding
                  draft[1].data = res.data.supplierEstimationBuilding
                  draft[2].data = res.data.completedBuilding
                  draft[3].data = res.data.otherCompanyBuildingDecision
                  draft[4].data = res.data.otherCompanyBuildingExpectation
                } else {
                  draft[0].data = res.data.supplierDecisionBuilding
                  draft[1].data = res.data.supplierEstimationBuilding
                  draft[2].data = res.data.completedBuilding
                  draft[3].data = res.data.unpublishedBuildingDecision
                  draft[4].data = res.data.unpublishedBuildingExpectation
                  draft[5].data = res.data.otherCompanyBuildingDecision
                  draft[6].data = res.data.otherCompanyBuildingExpectation
                }
              })
            )
            setSeries2(
              produce((draft) => {
                draft[0].data = res.data.supplierMaxCapacity
                draft[1].data = res.data.ourCompanyCapacity
              })
            )
          }
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
    return () => {
      ignore = true
    }
  }, [expenseCd, factoryCd, simulationYm, supplierCd])

  return (
    <div>
      <SupplierHearingHeader
        enableFixContentRegistration
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />
      <div>
        <Chart>
          <ChartLegend position="top" orientation="horizontal" />
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categories}
              labels={{
                rotation: 'auto'
              }}
            />
          </ChartCategoryAxis>
          <ChartValueAxis>
            <ChartValueAxisItem
              labels={{
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                content: (e) => e.value + '(' + unit + ')'
              }}
            />
          </ChartValueAxis>
          <ChartSeries>
            {series.map((item, index) => (
              <ChartSeriesItem
                key={`${item.name}_${index}`}
                type="column"
                tooltip={{
                  visible: true
                }}
                stack={{
                  group: 'symmary'
                }}
                data={item.data}
                name={item.name}
                color={item.color}
              />
            ))}
            {series2.map((item, index) => (
              <ChartSeriesItem
                key={`${item.name}_${index}`}
                type="line"
                tooltip={{
                  visible: true
                }}
                data={item.data}
                name={item.name}
                color={item.color}
              />
            ))}
          </ChartSeries>
        </Chart>
      </div>
      <ReplyAggregationGraphFooter />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </div>
  )
}
